import {MaybeRef, unref, computed} from 'vue';
import {useQuery} from '@tanstack/vue-query';

import {OrderApi} from "@/entities/Order";
import type {Order} from "@/entities/Order/model/types";

import * as UserApi from '../api';

import {activeTariffsKeys, userKeys} from "./keys";
import {formatCompanies} from "./formatCompanies";
import {isAuth} from "./authState";
import {resolveNoActiveTariff, type TariffType} from "./activeTariffError";

export type {TariffType};

/**
 * Результат запроса активного тарифа. Когда тариф есть — полный тариф + type, когда его
 * нет — только {active: false, type}. Поэтому поля тарифа опциональны (Partial<Order>).
 * Сбой запроса сюда не попадает: он уходит в isError (см. resolveNoActiveTariff).
 */
export type ActiveTariffData = Partial<Order> & {
    active: boolean;
    type: TariffType;
};

export const useActiveTariff = (uin: MaybeRef<string | undefined>) => {
    return useQuery({
        queryKey: computed(() => activeTariffsKeys.byUin(unref(uin))),
        queryFn: async (): Promise<ActiveTariffData | null> => {
            const uinValue = unref(uin);
            if (!uinValue) return null;
            try {
                const data = await OrderApi.getActiveOrder(uinValue);

                return {...data, type: data.tariff.maxNumOfDocs != 0 ? 'by_count' : 'by_date'};
            } catch (e) {
                const type = resolveNoActiveTariff(e);
                // Сбой запроса пробрасываем: пусть запрос встанет в isError, а не притворится
                // ответом «тарифа нет» — по нему закрывают создание документа и сносят черновик
                if (!type) throw e;

                return {
                    active: false,
                    type,
                };
            }
        },
        refetchInterval: 30000,
        retry: false,
    });
}

/**
 * Список компаний пользователя. Ключ без параметров: список глобален для сессии,
 * выбранная компания — отдельное клиентское состояние (см. useSelectedCompany).
 */
export const useCompaniesQuery = () => {
    return useQuery({
        queryKey: userKeys.companies(),
        queryFn: async () => formatCompanies(await UserApi.getCompanies()),
        enabled: isAuth,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export const useUserProfileQuery = () => {
    return useQuery({
        queryKey: userKeys.profile(),
        queryFn: () => UserApi.getUserProfile(),
        enabled: isAuth,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}
